import { createFileRoute, Link } from "@tanstack/react-router";
import { motion, useReducedMotion } from "framer-motion";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, Mic, Music, Beer, Clock, Phone, MapPin, Users, PartyPopper, Bus, Navigation, Train, Check } from "lucide-react";
import { SiteFooter } from "@/components/SiteFooter";
import { trackEvent } from "@/lib/analytics";
import { useReservationModal } from "@/context/ReservationContext";
import { listKaraokePackages, type KaraokePackage } from "@/lib/karaoke.functions";
import karaokeWide from "@/assets/karaoke-31.webp";
import karaokeScreen from "@/assets/karaoke-32.webp";
import karaokeStage from "@/assets/karaoke-33.webp";
import karaokeSeating from "@/assets/karaoke-34.webp";
import karaokeStageNeon from "@/assets/karaoke-35.webp";

const packagesQuery = queryOptions({
  queryKey: ["karaoke-packages"],
  queryFn: () => listKaraokePackages(),
  staleTime: 60_000,
});

const OG_IMAGE = "https://plunkets.ie/og-karaoke.jpg";
// Plunkets Pub — 21 Middle Abbey Street, Dublin 1
const PLUNKETS_COORDS = "53.347879,-6.260114";
const DIRECTIONS_URL = `https://www.google.com/maps/dir/?api=1&destination=${PLUNKETS_COORDS}&destination_place_id=Plunkets+Pub+Dublin`;
const TRANSIT_URL = `https://www.google.com/maps/dir/?api=1&destination=${PLUNKETS_COORDS}&destination_place_id=Plunkets+Pub+Dublin&travelmode=transit`;

const FAQS = [
  {
    q: "Where is the best karaoke in Dublin city centre?",
    a: "Plunkets Pub on Middle Abbey Street (Dublin 1) runs an open karaoke terrace with a 10,000-song library, table service and full cocktail menu — a 3-minute walk from O'Connell Street.",
  },
  {
    q: "Can I book the karaoke terrace in Dublin?",
    a: "Yes. The Karaoke Terrace is an open terrace area you can reserve a spot in for your group. Book online for parties of 6–30, or call us to arrange bigger takeovers.",
  },
  {
    q: "How much does karaoke cost at Plunkets?",
    a: "The Karaoke Terrace is free to reserve with a food & drink minimum spend that depends on group size and night. Peak weekend slots require a small deposit — details are shown when you book.",
  },
  {
    q: "How late is karaoke open in Dublin?",
    a: "Plunkets is open until 23:30 daily, 00:30 Fri & Sat. Karaoke slots run from the afternoon through late night — Thursday to Sunday are the busiest, so book ahead.",
  },
];

export const Route = createFileRoute("/karaoke-dublin")({
  loader: ({ context }) => context.queryClient.ensureQueryData(packagesQuery),
  head: () => ({
    meta: [
      { title: "Karaoke Terrace Dublin City Centre — Karaoke Nights | Plunkets Pub" },
      { name: "description", content: "Karaoke Terrace in Dublin city centre. 10,000+ song library, big screens, table service and cocktails at Plunkets, Middle Abbey Street, Dublin 1. Book online." },
      { name: "keywords", content: "karaoke Dublin, karaoke Dublin city centre, karaoke terrace Dublin, karaoke bar Dublin, karaoke night Dublin, karaoke pub Dublin" },
      { property: "og:title", content: "Karaoke Terrace in Dublin City Centre — Plunkets" },
      { property: "og:description", content: "Karaoke Terrace, 10,000+ songs and table service in Dublin 1." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://plunkets.ie/karaoke-dublin" },
      { property: "og:image", content: OG_IMAGE },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:locale", content: "en_IE" },
      { property: "og:site_name", content: "Plunkets Pub Dublin" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Karaoke Terrace in Dublin City Centre — Plunkets" },
      { name: "twitter:description", content: "Karaoke Terrace, 10,000+ songs, cocktails at your table." },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: "https://plunkets.ie/karaoke-dublin" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BarOrPub",
          name: "Plunkets Pub — Karaoke Terrace",
          url: "https://plunkets.ie/karaoke-dublin",
          image: OG_IMAGE,
          telephone: "+353 1 000 0000",
          address: {
            "@type": "PostalAddress",
            streetAddress: "Middle Abbey Street",
            addressLocality: "Dublin",
            postalCode: "D01",
            addressCountry: "IE",
          },
          amenityFeature: [
            { "@type": "LocationFeatureSpecification", name: "Karaoke Terrace", value: true },
            { "@type": "LocationFeatureSpecification", name: "Sound-proofed karaoke terrace", value: true },
            { "@type": "LocationFeatureSpecification", name: "10,000+ song library", value: true },
            { "@type": "LocationFeatureSpecification", name: "Table service", value: true },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          about: "Karaoke Terrace Dublin",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: KaraokePage,
});

function euros(cents: number) {
  return `€${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;
}

function KaraokePage() {
  const reduce = useReducedMotion();
  const { open: openModal } = useReservationModal();
  const { data: packages = [] } = useSuspenseQuery(packagesQuery);

  const features = [
    { icon: Mic, title: "Karaoke Terrace", desc: "Your crew, your playlist, your stage — proper mics and big screens out on the terrace." },
    { icon: Music, title: "10,000+ Song Library", desc: "Classics, chart hits, Irish anthems, guilty pleasures. Search by artist, song or decade on the touchscreen." },
    { icon: Beer, title: "Table Service", desc: "Cocktail rounds and pints delivered to the room so no one loses their turn at the mic." },
    { icon: Clock, title: "Open Until 23:30", desc: "Open every day. Karaoke available afternoons and late — book a slot Thu–Sun for peak nights." },
    { icon: Users, title: "Groups from 6 to 30", desc: "Perfect for birthdays, hens, stags, work nights, tour groups or a Thursday that just gets away from you." },
    { icon: PartyPopper, title: "Right in the City Centre", desc: "Middle Abbey Street, Dublin 1 — 3 minutes from O'Connell Street, 5 from Temple Bar." },
  ];

  const gallery = [
    { src: karaokeScreen, alt: "Karaoke screen and microphone on the Karaoke Terrace at Plunkets Pub Dublin" },
    { src: karaokeStage, alt: "Neon-lit karaoke stage and high tables at Plunkets Dublin city centre" },
    { src: karaokeSeating, alt: "Booth seating and low tables on the Karaoke Terrace at Plunkets Dublin 1" },
    { src: karaokeStageNeon, alt: "Karaoke stage with neon floor lighting and big screen at Plunkets Dublin" },
  ];

  const faqs = FAQS;

  return (
    <main className="relative min-h-screen bg-background text-foreground">
      {/* HERO BANNER */}
      <section
        aria-labelledby="karaoke-hero-title"
        className="relative flex min-h-[62vh] items-end justify-center overflow-hidden sm:min-h-[68vh] md:min-h-[72vh] lg:min-h-[78vh]"
      >
        <img
          src={karaokeWide}
          alt="Karaoke Terrace in Dublin city centre at Plunkets Pub"
          title="Karaoke Terrace in Dublin city centre at Plunkets Pub"
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover object-center"
        />
        <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-background via-background/75 to-background/25 sm:via-background/60 sm:to-transparent" />
        <div aria-hidden="true" className="absolute inset-0 bg-background/25 sm:bg-transparent" />
        <div className="relative z-10 mx-auto w-full max-w-4xl px-5 pb-10 pt-28 sm:px-6 sm:pb-14 sm:pt-32 md:px-8 md:pb-20 md:pt-40">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-primary transition-colors">
            <ArrowLeft size={16} /> Back to Plunkets
          </Link>
          <motion.div
            initial={reduce ? undefined : { opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <p className="mt-5 text-[10px] uppercase tracking-[0.35em] text-primary sm:mt-6 sm:text-xs sm:tracking-[0.4em]">Karaoke Terrace · Dublin 1</p>
            <h1
              id="karaoke-hero-title"
              className="mt-3 font-display text-[2.25rem] leading-[1.02] tracking-tight text-white sm:text-5xl md:text-6xl lg:text-7xl"
            >
              Karaoke Terrace <span className="text-gold-gradient">in Dublin City Centre</span>
            </h1>
            <p className="mt-4 max-w-2xl text-sm text-white/85 sm:text-base md:text-lg">
              Plunkets' Karaoke Terrace is Dublin city centre's easy answer to "where next?" — a proper stage,
              a 10,000-song library, table service and cocktails all under one roof on Middle
              Abbey Street.
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:mt-8 sm:flex-row sm:items-center">
              <button
                onClick={() => openModal({ bookingType: "group_booking" })}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3.5 text-sm font-semibold uppercase tracking-wider text-primary-foreground shadow-lg shadow-black/30 transition-all hover:opacity-90 sm:w-auto"
              >
                Book the Karaoke Terrace <ArrowRight size={16} />
              </button>
              <a
                href="tel:+35310000000"
                className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-white/25 bg-white/5 px-6 py-3.5 text-sm font-semibold uppercase tracking-wider text-white backdrop-blur-sm transition-colors hover:bg-white/10 sm:w-auto"
              >
                <Phone size={16} /> Call to Enquire
              </a>
              <a
                href={DIRECTIONS_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  trackEvent("directions_click", {
                    location: "karaoke-dublin-hero",
                    travel_mode: "driving",
                    destination: "Plunkets Pub Dublin",
                  })
                }
                className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-white/25 bg-white/5 px-6 py-3.5 text-sm font-semibold uppercase tracking-wider text-white backdrop-blur-sm transition-colors hover:bg-white/10 sm:w-auto"
              >
                <Navigation size={16} /> Get Directions
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="relative px-5 py-16 md:px-8 md:py-20">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-10 lg:grid-cols-2 lg:gap-16 lg:items-center">
            <div>
              <h2 className="font-display text-3xl font-bold leading-tight md:text-5xl">
                Grab the mic. <span className="text-gold-gradient">Own the night.</span>
              </h2>
              <p className="mt-5 max-w-lg text-base text-muted-foreground md:text-lg">
                Whether it's a birthday, a hen night, a work send-off or a Thursday that turned into a session,
                our Karaoke Terrace is built for it — proper sound, big screens and cocktails at your table.
              </p>
              <ul className="mt-8 grid gap-3 sm:grid-cols-2">
                {features.map((f) => (
                  <li key={f.title} className="flex items-start gap-3 rounded-xl border border-border bg-card/50 p-4 backdrop-blur">
                    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-primary/40 text-primary">
                      <f.icon size={16} />
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-foreground">{f.title}</p>
                      <p className="mt-1 text-xs text-muted-foreground">{f.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="grid grid-cols-2 gap-3 md:gap-4">
              {gallery.map((g, i) => (
                <figure
                  key={g.src}
                  className={`group relative overflow-hidden rounded-2xl border border-border bg-card/40 ${i === 0 ? "col-span-2 aspect-[16/10]" : "aspect-square"}`}
                >
                  <img src={g.src} alt={g.alt} loading="lazy" decoding="async" width={i === 0 ? 800 : 400} height={i === 0 ? 500 : 400} className="h-full w-full object-cover transition duration-500 group-hover:scale-105" />
                </figure>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="relative border-t border-border bg-card/30 px-5 py-16 md:px-8 md:py-20">
        <div className="mx-auto max-w-7xl">
          <h2 className="font-display text-3xl font-bold md:text-4xl">How the Karaoke Terrace works at Plunkets</h2>
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            {[
              { n: "1", t: "Reserve your slot", d: "Book online for parties of 6–30. Peak weekend slots need a small deposit that comes off your bill." },
              { n: "2", t: "Show up and sing", d: "The terrace is set up and ready — mics, screens and 10,000+ songs at your fingertips." },
              { n: "3", t: "We keep the round going", d: "Table service means cocktail pitchers and pints land without anyone leaving the room." },
            ].map((s) => (
              <div key={s.n} className="rounded-2xl border border-border bg-background/60 p-6">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-primary/40 font-display text-sm text-primary">{s.n}</span>
                <p className="mt-4 font-semibold text-foreground">{s.t}</p>
                <p className="mt-2 text-sm text-muted-foreground">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OPTIONAL KARAOKE PACKAGES */}
      {packages.length > 0 && (
        <section className="relative border-t border-border px-5 py-16 md:px-8 md:py-20">
          <div className="mx-auto max-w-7xl">
            <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.28em] text-primary">Optional Add-Ons</p>
                <h2 className="mt-3 font-display text-3xl font-bold md:text-4xl">
                  Karaoke Packages <span className="text-gold-gradient">(optional)</span>
                </h2>
                <p className="mt-3 max-w-2xl text-base text-muted-foreground md:text-lg">
                  Add food, drinks and guaranteed terrace time to your karaoke booking. Pick a package when you reserve, or mix and match on the night.
                </p>
              </div>
              <button
                onClick={() => openModal({ bookingType: "group_booking" })}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-3.5 text-sm font-semibold uppercase tracking-wider text-primary-foreground shadow-lg shadow-black/30 transition-all hover:opacity-90"
              >
                Book with a package <ArrowRight size={16} />
              </button>
            </div>

            <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {packages.map((p: KaraokePackage) => (
                <div
                  key={p.id}
                  className="flex flex-col rounded-2xl border border-border bg-card/40 p-6 transition hover:border-primary/40 hover:bg-card/60"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-display text-xl font-bold">{p.name}</h3>
                      <p className="mt-1 text-xs text-muted-foreground">{p.tagline}</p>
                    </div>
                    <span className="shrink-0 rounded-full bg-primary/10 px-3 py-1 text-sm font-bold text-primary">
                      {euros(p.price_cents)} {p.price_unit === "per_group" ? "/ group" : "pp"}
                    </span>
                  </div>
                  <p className="mt-4 flex-1 text-sm text-muted-foreground">{p.description}</p>
                  <div className="mt-4 flex flex-wrap gap-2 text-xs text-muted-foreground">
                    <span className="rounded-full border border-border px-2.5 py-1">{p.min_guests}–{p.max_guests} guests</span>
                    <span className="rounded-full border border-border px-2.5 py-1">{p.duration_minutes} min</span>
                  </div>
                  {p.includes.length > 0 && (
                    <ul className="mt-5 space-y-2 border-t border-border pt-5">
                      {p.includes.map((item) => (
                        <li key={item} className="flex items-start gap-2 text-sm text-foreground">
                          <Check size={14} className="mt-0.5 shrink-0 text-primary" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="relative px-5 py-16 md:px-8 md:py-20">
        <div className="mx-auto max-w-4xl">
          <h2 className="font-display text-3xl font-bold md:text-4xl">Karaoke Terrace in Dublin — FAQ</h2>
          <div className="mt-8 space-y-4">
            {faqs.map((f) => (
              <details key={f.q} className="group rounded-xl border border-border bg-card/40 p-5">
                <summary className="cursor-pointer list-none font-semibold text-foreground">{f.q}</summary>
                <p className="mt-3 text-sm text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* LOCATION & TRANSPORT */}
      <section
        aria-labelledby="karaoke-location-title"
        className="relative border-t border-border bg-card/30 px-5 py-16 md:px-8 md:py-20"
      >
        <div className="mx-auto max-w-5xl">
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-primary">Getting Here</p>
          <h2 id="karaoke-location-title" className="mt-3 font-display text-3xl font-bold md:text-4xl">
            Karaoke Terrace in Dublin — <span className="text-gold-gradient">right on Middle Abbey Street</span>
          </h2>
          <p className="mt-4 max-w-2xl text-base text-muted-foreground md:text-lg">
            3 minutes on foot from O'Connell Street and 5 from Temple Bar — easy to reach by Luas, DART or Dublin Bus, and step-free from the front door.
          </p>

          <div className="mt-8 grid gap-4 sm:grid-cols-3">
            <div className="rounded-2xl border border-border bg-background/60 p-6">
              <Train size={22} className="text-primary" />
              <p className="mt-4 font-display text-base font-bold">Luas Red Line — Abbey Street</p>
              <p className="mt-1 text-sm text-muted-foreground">1-minute walk. Trams every 5–8 minutes to Connolly, Heuston and The Point.</p>
            </div>
            <div className="rounded-2xl border border-border bg-background/60 p-6">
              <Train size={22} className="text-primary" />
              <p className="mt-4 font-display text-base font-bold">DART / Rail — Connolly Station</p>
              <p className="mt-1 text-sm text-muted-foreground">8-minute walk. Direct DART line along the coast, plus Intercity to Belfast, Sligo and Rosslare.</p>
            </div>
            <div className="rounded-2xl border border-border bg-background/60 p-6">
              <Bus size={22} className="text-primary" />
              <p className="mt-4 font-display text-base font-bold">Dublin Bus — O'Connell Street</p>
              <p className="mt-1 text-sm text-muted-foreground">2-minute walk. Dozens of city and suburban routes, plus 24-hour Nitelink at weekends.</p>
            </div>
          </div>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            <a
              href={TRANSIT_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() =>
                trackEvent("public_transport_directions_click", {
                  location: "karaoke-dublin",
                  travel_mode: "transit",
                  destination: "Plunkets Pub Dublin",
                })
              }
              className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-3.5 text-sm font-semibold uppercase tracking-wider text-primary-foreground shadow-lg shadow-black/30 transition-all hover:opacity-90"
            >
              <Bus size={16} /> Public Transport Directions
            </a>
            <a
              href={DIRECTIONS_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() =>
                trackEvent("directions_click", {
                  location: "karaoke-dublin",
                  travel_mode: "driving",
                  destination: "Plunkets Pub Dublin",
                })
              }
              className="inline-flex items-center justify-center gap-2 rounded-full border border-primary/40 bg-background/60 px-6 py-3.5 text-sm font-semibold uppercase tracking-wider text-foreground transition-colors hover:bg-primary/10"
            >
              <Navigation size={16} /> Open in Google Maps
            </a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative border-t border-border px-5 py-16 md:px-8 md:py-20">
        <div className="mx-auto flex max-w-5xl flex-col items-start gap-6 rounded-3xl border border-primary/30 bg-card/60 p-8 md:flex-row md:items-center md:justify-between md:p-12">
          <div>
            <h2 className="font-display text-2xl font-bold md:text-3xl">Ready to book your Karaoke Terrace night?</h2>
            <p className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
              <MapPin size={14} /> Middle Abbey Street, Dublin 1 · Open until 23:30 daily · 00:30 Fri & Sat
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => openModal({ bookingType: "group_booking" })}
              className="btn-amber inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold"
            >
              Book Karaoke Terrace <ArrowRight size={16} />
            </button>
            <Link to="/events" className="btn-ghost-gold inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold">
              See What's On
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </main>
  );
}
